import { sdk } from '@/lib/api';
import { FetchError } from '@/lib/api/client';
import { queryKeysFactory } from '@/lib/query-key-factory';
import { HttpTypes } from '@/types';
import { ICategory } from '@/types/category';
import { BaseProduct } from '@/types/http/product/common';
import { IProduct, IVariantType, ProductItemExtended } from '@/types/product';
import {
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import Cookies from 'js-cookie';

const PRODUCT_UNIT_QUERY_KEY = 'product-unit' as const;
const PRODUCT_UNIT_ITEM_QUERY_KEY = 'product-item' as const;
const PRODUCT_CATEGORY_QUERY_KEY = 'product-category' as const;
const PRODUCT_VARIANT_TYPE_QUERY_KEY = 'product-variant-type' as const;
const PRODUCT_LOG_QUERY_KEY = 'product-log' as const;
const PRODUCT_IMPORT_QUERY_KEY = 'product-import' as const;
const PRODUCT_IMPORT_HISTORY_QUERY_KEY = 'product-import-history' as const;

export const productsQueryKeys = queryKeysFactory(PRODUCT_UNIT_QUERY_KEY);
export const productUnitItemQueryKeys = queryKeysFactory(PRODUCT_UNIT_ITEM_QUERY_KEY);
export const productCategoryQueryKeys = queryKeysFactory(PRODUCT_CATEGORY_QUERY_KEY);
export const productVariantTypeQueryKeys = queryKeysFactory(PRODUCT_VARIANT_TYPE_QUERY_KEY);
export const productLogQueryKeys = queryKeysFactory(PRODUCT_LOG_QUERY_KEY);
export const productImportQueryKeys = queryKeysFactory(PRODUCT_IMPORT_QUERY_KEY);
export const productImportHistoryQueryKeys = queryKeysFactory(PRODUCT_IMPORT_HISTORY_QUERY_KEY);

const isPartner = Cookies.get('_partner_id') !== undefined;

export const useProducts = (
  query: unknown,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.AdminProductListResponse,
      FetchError,
      HttpTypes.AdminProductListResponse,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: productsQueryKeys.list(query),
    queryFn: async () =>
      isPartner ? sdk.partner.products.list(query) : sdk.admin.products.list(query),
    ...options,
  });
  return { ...data, ...rest };
};

export const useDetailProduct = (
  id: string,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.IResponse<BaseProduct>,
      FetchError,
      HttpTypes.IResponse<BaseProduct>,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: productsQueryKeys.detail(id),
    queryFn: async () =>
      isPartner ? sdk.partner.products.detail(id) : sdk.admin.products.detail(id),
    ...options,
  });
  return { ...data, ...rest };
};

export const useCreateProduct = (
  options?: UseMutationOptions<
    HttpTypes.IResponse<HttpTypes.AdminProductCreateResponse>,
    FetchError,
    HttpTypes.AdminProductCreatePayload
  >
) => {
  return useMutation({
    mutationFn: (payload) =>
      isPartner ? sdk.partner.products.create(payload) : sdk.admin.products.create(payload),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useUpdateProduct = (
  options?: UseMutationOptions<HttpTypes.IResponse<BaseProduct>, FetchError, BaseProduct>
) => {
  return useMutation({
    mutationFn: (payload) =>
      isPartner ? sdk.partner.products.update(payload) : sdk.admin.products.update(payload),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useDeleteProductUnitItems = (
  options?: UseMutationOptions<
    HttpTypes.IResponse<IProduct>,
    FetchError,
    { id: string; item_ids: string[] }
  >
) => {
  return useMutation({
    mutationFn: (payload) =>
      isPartner
        ? sdk.partner.products.deleteUnitItems(payload)
        : sdk.admin.products.deleteUnitItems(payload),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useDeleteProductGroup = (
  options?: UseMutationOptions<HttpTypes.IResponse<IProduct>, FetchError, { ids: string[] }>
) => {
  return useMutation({
    mutationFn: (payload) =>
      isPartner
        ? sdk.partner.products.deleteGroup(payload)
        : sdk.admin.products.deleteGroup(payload),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useUpdateStatusProductMultiple = (
  options?: UseMutationOptions<
    HttpTypes.IResponse<IProduct>,
    FetchError,
    { ids: string[]; is_active: boolean }
  >
) => {
  return useMutation({
    mutationFn: (payload) =>
      isPartner
        ? sdk.partner.products.updateStatusMultiple(payload)
        : sdk.admin.products.updateStatusMultiple(payload),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useInfiniteProducts = (
  query: HttpTypes.FindParams,
  options?: Omit<
    UseInfiniteQueryOptions<
      HttpTypes.StoreProductListResponse,
      FetchError,
      HttpTypes.StoreProductListResponse,
      HttpTypes.StoreProductListResponse,
      QueryKey
    >,
    'queryFn' | 'queryKey' | 'getNextPageParam' | 'initialPageParam'
  >
) => {
  return useInfiniteQuery({
    queryKey: productsQueryKeys.list(query),
    queryFn: async ({ pageParam = 1 }) =>
      isPartner
        ? sdk.partner.products.list({ ...query, page: pageParam as number })
        : sdk.admin.products.list({ ...query, page: pageParam as number }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const { data, totalCount } = lastPage;
      const currentPage = allPages.length;
      const pageSize = data.length;

      if (currentPage * pageSize < Number(totalCount)) {
        return currentPage + 1;
      }
      return undefined;
    },
    ...options,
  });
};

export const useGetProductItem = (
  productId: string,
  query?: HttpTypes.FindParams,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.IResponse<IProduct>,
      FetchError,
      HttpTypes.IResponse<IProduct>,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: [...productUnitItemQueryKeys.list(productId), query],
    queryFn: async () =>
      isPartner
        ? sdk.partner.products.listItem(productId, query as HttpTypes.FindParams)
        : sdk.admin.products.listItem(productId, query as HttpTypes.FindParams),
    ...options,
  });
  return { data, ...rest };
};

export const useGetProductCategoryChildId = (
  id: string,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.IResponse<ICategory[]>,
      FetchError,
      HttpTypes.IResponse<ICategory[]>,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: productCategoryQueryKeys.list(id),
    queryFn: async () =>
      isPartner
        ? sdk.partner.products.listParentCategoryByChildId(id)
        : sdk.admin.products.listParentCategoryByChildId(id),
    enabled: !!id && id.trim() !== '',
    ...options,
  });

  return { data, ...rest };
};

export const useGetProductChildCategoryByParentId = (
  parentId: string,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.AdminProductCategoryListResponse,
      FetchError,
      HttpTypes.AdminProductCategoryListResponse,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: productCategoryQueryKeys.list(parentId),
    queryFn: async () =>
      isPartner
        ? sdk.partner.products.listChildCategoryByParentId(parentId)
        : sdk.admin.products.listChildCategoryByParentId(parentId),
    enabled: !!parentId && (options?.enabled ?? true),
    ...options,
  });

  return { ...data, ...rest };
};

export const useGetProductParentCategory = (
  query?: HttpTypes.FindParams,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.IResponse<ICategory[]>,
      FetchError,
      HttpTypes.IResponse<ICategory[]>,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: productCategoryQueryKeys.list(),
    queryFn: async () =>
      isPartner
        ? sdk.partner.products.listAllParentCategory(query as HttpTypes.FindParams)
        : sdk.admin.products.listAllParentCategory(query as HttpTypes.FindParams),
    ...options,
  });

  return { data, ...rest };
};

export const useGetAllCategory = (
  query?: HttpTypes.FindParams,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.AdminProductCategoryListResponse,
      FetchError,
      HttpTypes.AdminProductCategoryListResponse,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: [productCategoryQueryKeys.list(), query],
    queryFn: async () =>
      isPartner
        ? sdk.partner.products.listAllCategory(query as HttpTypes.FindParams)
        : sdk.admin.products.listAllCategory(query as HttpTypes.FindParams),
    ...options,
  });

  return { data, ...rest };
};

export const useGetAllCategoryTree = (
  id: string,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.AdminProductCategoryTreeListResponse,
      FetchError,
      HttpTypes.AdminProductCategoryTreeListResponse,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: productCategoryQueryKeys.list(id),
    queryFn: async () =>
      isPartner
        ? sdk.partner.products.listCategoryTree(id)
        : sdk.admin.products.listCategoryTree(id),
    enabled: !!id && (options?.enabled ?? true),
    ...options,
  });

  return { data, ...rest };
};

export const useGetProductVariantType = (
  options?: Omit<
    UseQueryOptions<
      HttpTypes.IResponse<IVariantType[]>,
      FetchError,
      HttpTypes.IResponse<IVariantType[]>,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: productVariantTypeQueryKeys.list(),
    queryFn: async () =>
      isPartner ? sdk.partner.products.listVariantType() : sdk.admin.products.listVariantType(),
    ...options,
  });

  return { data, ...rest };
};

export const useRetrieveProductItem = (
  id: string,
  query?: HttpTypes.FindParams,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.IResponse<ProductItemExtended>,
      FetchError,
      HttpTypes.IResponse<ProductItemExtended>,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: productUnitItemQueryKeys.list(id),
    queryFn: async () =>
      isPartner
        ? sdk.partner.products.retrieveProductItemDetails(id, query as HttpTypes.FindParams)
        : sdk.admin.products.retrieveProductItemDetails(id, query as HttpTypes.FindParams),
    ...options,
  });

  return { data, ...rest };
};

export const useAddProductCollection = (
  options?: UseMutationOptions<
    HttpTypes.IResponse<IProduct>,
    FetchError,
    { product_ids: string[]; collection_id: string }
  >
) => {
  return useMutation({
    mutationFn: (payload) => sdk.admin.products.addProductCollection(payload),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useExportProduct = (
  options?: UseMutationOptions<
    HttpTypes.AdminProductExportResponse,
    FetchError,
    HttpTypes.AdminProductExportPayload
  >
) => {
  return useMutation({
    mutationFn: (payload) => sdk.admin.products.exportProduct(payload),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useGetProductLogs = (
  productId: string,
  query: HttpTypes.FindParams,
  options?: Omit<
    UseInfiniteQueryOptions<
      HttpTypes.AdminProductLogListResponse,
      FetchError,
      HttpTypes.AdminProductLogListResponse,
      HttpTypes.AdminProductLogListResponse,
      QueryKey
    >,
    'queryFn' | 'queryKey' | 'getNextPageParam' | 'initialPageParam'
  >
) => {
  return useInfiniteQuery({
    queryKey: productLogQueryKeys.list(productId),
    queryFn: async ({ pageParam = 1 }) =>
      sdk.admin.products.listLog(productId, { ...query, page: pageParam as number }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const { data, totalCount } = lastPage;
      const currentPage = allPages.length;
      const pageSize = data.length;

      if (currentPage * pageSize < Number(totalCount)) {
        return currentPage + 1;
      }
      return undefined;
    },
    ...options,
  });
};

export const useAddProductToMultipleCollections = (
  options?: UseMutationOptions<
    HttpTypes.IResponse<IProduct>,
    FetchError,
    { product_ids: string[]; collection_ids: string[] }
  >
) => {
  return useMutation({
    mutationFn: (payload) => sdk.admin.products.addProductToMultipleCollections(payload),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useImportProduct = (
  options?: UseMutationOptions<HttpTypes.IResponse<IProduct>, FetchError, FormData>
) => {
  return useMutation({
    mutationFn: (payload) => sdk.admin.products.importProduct(payload),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useImportProductHistory = (
  query: HttpTypes.FindParams,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.AdminProductEximResponse,
      FetchError,
      HttpTypes.AdminProductEximResponse,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: productImportQueryKeys.list(query),
    queryFn: async () => sdk.admin.products.importProductHistory(query),
    ...options,
  });

  return { data, ...rest };
};

export const useImportProductHistoryDetail = (
  id: string,
  query: HttpTypes.FindParams,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.AdminProductEximDetailResponse,
      FetchError,
      HttpTypes.AdminProductEximDetailResponse,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: productImportHistoryQueryKeys.list(query),
    queryFn: async () => sdk.admin.products.importProductHistoryDetail(id, query),
    ...options,
  });

  return { data, ...rest };
};
